import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useNavigate, useOutletContext, useParams, useSearchParams } from '@remix-run/react';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast from '~/hooks/useToast';
import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Publication } from '~/interfaces/publication';
import toast from '~/utils/toast';

enum ToastStatus {
  SUCCESS = 'success',
  ERROR = 'error',
}

// Unsubscribes are often accidental, so the confirmation stays up long enough to notice.
const UNSUBSCRIBE_CONFIRMATION_TOAST_DURATION_MS = 15_000;

type Props = {
  publication: Publication;
  scope?: 'everything';
  showSubmitButton?: boolean;
  onSuccess?: () => void;
  children?: ({ isLoading, submitForm }: { isLoading: boolean; submitForm: () => void }) => ReactNode;
};

const UnsubscribeAction = ({ publication, scope, children, onSuccess, showSubmitButton = true }: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher();
  const params = useParams();
  const { subscriberId } = params;
  const fetcherData = fetcher.data as {
    postUnsubscribeFormId: string;
    toast: { status: ToastStatus; message: string };
  };
  const isSubmitting = fetcher.state === 'submitting';
  const formRef = useRef<HTMLFormElement>(null);
  const { removeCookie, cookies } = useOutletContext<OutletContext>();
  const status = fetcherData?.toast?.status;
  const navigate = useNavigate();

  const isSuccess = status === ToastStatus.SUCCESS;

  useEffect(() => {
    if (fetcherData?.postUnsubscribeFormId) {
      navigate(`/forms/${fetcherData?.postUnsubscribeFormId}`);
    }
  }, [fetcherData?.postUnsubscribeFormId, navigate]);

  useToast(isSuccess ? undefined : fetcherData?.toast);

  useEffect(() => {
    if (!isSuccess) return;

    toast.success(fetcherData.toast.message, { duration: UNSUBSCRIBE_CONFIRMATION_TOAST_DURATION_MS });
  }, [fetcherData, isSuccess]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want this to run on every render
  useEffect(() => {
    if (isSuccess) {
      removeCookie(ACTIVE_UPGRADE_COOKIE_NAME);
      onSuccess?.();
    }
  }, [status]);

  const submitForm = () => {
    if (formRef.current) {
      fetcher.submit(formRef.current);
    }
  };

  return (
    <fetcher.Form ref={formRef} method="post" action="/unsubscribe">
      <input hidden name="subscriber_id" defaultValue={subscriberId} />
      <input hidden name="unsubscribe_form_id" defaultValue={publication.post_unsubscribe_form_id || ''} />
      {searchParams.has('post_id') && <input hidden name="post_id" defaultValue={searchParams.get('post_id') || ''} />}
      <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid ?? ''} />
      {scope && <input hidden name="scope" defaultValue={scope} />}
      <StatusInputs
        name="unsubscribe"
        errorMessage={t('account_actions.downgrade.toast.error')}
        successMessage={t('account_actions.downgrade.toast.fully_unsubscribed')}
      />
      {showSubmitButton && (
        <Button type="submit" variant="danger" isLoading={isSubmitting} btnSize="md">
          {isSubmitting ? `${t('shared.actions.unsubscribing')}…` : t('shared.cta.unsubscribe')}
        </Button>
      )}
      {children?.({ isLoading: isSubmitting, submitForm })}
    </fetcher.Form>
  );
};

export default UnsubscribeAction;
